import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, QrCode, RefreshCw, FileDown, AlertTriangle, LayoutGrid, FileImage } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE as API } from '@/utils/api';
import PosterDesigner from '@/components/PosterDesigner';
import { TemplateGallery } from '@/components/qrcenter/TemplateGallery';
import { UnitsPanel } from '@/components/qrcenter/UnitsPanel';
import { SheetConfigPanel } from '@/components/qrcenter/SheetConfigPanel';
import { SheetLivePreview } from '@/components/qrcenter/SheetLivePreview';
import { SavedDesignsPanel, designToState } from '@/components/qrcenter/SavedDesignsPanel';

const HEX = /^#[0-9A-Fa-f]{6}$/;

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function QRCenter() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [business, setBusiness] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [units, setUnits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [unitTemplates, setUnitTemplates] = useState({});
  const [filterSpace, setFilterSpace] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [templateId, setTemplateId] = useState(null);
  const [config, setConfig] = useState({
    perPage: '4', showBusinessName: true, showSpaceName: false, showUnitName: true,
    showInstructions: true, instructionsText: 'Escaneá y pedí', wifiText: '', socialText: '', qrColor: '#111111',
    cutMarks: true, logoSource: 'none'
  });
  const [customLogo, setCustomLogo] = useState({ id: null, preview: null });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [designs, setDesigns] = useState([]);
  const [savingDesign, setSavingDesign] = useState(false);
  const [reprintingId, setReprintingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [unitsRes, tplRes, designsRes] = await Promise.all([
        axios.get(`${API}/qr-center/units`, { headers }),
        axios.get(`${API}/poster-designer/templates`, { headers }),
        axios.get(`${API}/qr-center/saved-designs`, { headers })
      ]);
      setDesigns(designsRes.data);
      setBusiness(unitsRes.data.business);
      if (unitsRes.data.business?.logo_url) setConfig(c => ({ ...c, logoSource: c.logoSource === 'none' ? 'business' : c.logoSource }));
      setSpaces(unitsRes.data.spaces);
      setUnits(unitsRes.data.units);
      setTemplates(tplRes.data.templates || []);
      setCategories(tplRes.data.categories || {});
      setTemplateId(prev => prev || tplRes.data.templates?.[0]?.template_id || null);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mantener el orden de la lista de unidades
  const orderedSelected = useMemo(
    () => units.filter(u => selectedUnits.includes(u.unit_id)).map(u => u.unit_id),
    [units, selectedUnits]
  );

  const payload = useMemo(() => ({
    unit_ids: orderedSelected,
    template_id: templateId,
    unit_templates: Object.fromEntries(Object.entries(unitTemplates).filter(([id]) => orderedSelected.includes(id))),
    per_page: config.perPage,
    show_business_name: config.showBusinessName,
    show_space_name: config.showSpaceName,
    show_unit_name: config.showUnitName,
    show_instructions: config.showInstructions,
    instructions_text: config.instructionsText,
    wifi_text: config.wifiText,
    social_text: config.socialText,
    qr_color: HEX.test(config.qrColor) ? config.qrColor : '#111111',
    cut_marks: config.cutMarks,
    logo_source: config.logoSource === 'custom' && !customLogo.id ? 'none' : config.logoSource,
    custom_logo_id: customLogo.id
  }), [orderedSelected, templateId, unitTemplates, config, customLogo.id]);

  useEffect(() => { setPage(1); }, [config.perPage, orderedSelected.length]);

  // Vista previa en vivo (con debounce)
  useEffect(() => {
    if (!templateId) return;
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);
        const res = await axios.post(`${API}/qr-center/sheet-preview`, { ...payload, page }, { headers, signal: ctrl.signal });
        setPreview(res.data);
      } catch (err) {
        if (!axios.isCancel(err)) toast.error('No se pudo actualizar la vista previa');
      } finally {
        if (!ctrl.signal.aborted) setPreviewLoading(false);
      }
    }, 450);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [payload, page, templateId, headers]);

  const toggleUnit = (id) => setSelectedUnits(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const selectAll = (list) => setSelectedUnits(p => Array.from(new Set([...p, ...list.map(u => u.unit_id)])));
  const setUnitTemplate = (id, tpl) => setUnitTemplates(p => {
    const next = { ...p };
    if (tpl) next[id] = tpl; else delete next[id];
    return next;
  });

  const existingIds = (ids) => ids.filter(id => units.some(u => u.unit_id === id));

  const saveDesign = async (name, includeUnits) => {
    try {
      setSavingDesign(true);
      const res = await axios.post(`${API}/qr-center/saved-designs`, { name, include_units: includeUnits, config: payload }, { headers });
      setDesigns(d => [res.data, ...d]);
      toast.success('Diseño guardado');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar el diseño');
      return false;
    }
    finally { setSavingDesign(false); }
  };

  const updateDesign = async (design) => {
    try {
      const res = await axios.put(`${API}/qr-center/saved-designs/${design.design_id}`,
        { name: design.name, include_units: design.include_units && orderedSelected.length > 0, config: payload }, { headers });
      setDesigns(d => [res.data, ...d.filter(x => x.design_id !== design.design_id)]);
      toast.success(`“${design.name}” actualizado con la configuración actual`);
    } catch { toast.error('No se pudo actualizar el diseño'); }
  };

  const deleteDesign = async (design) => {
    try {
      await axios.delete(`${API}/qr-center/saved-designs/${design.design_id}`, { headers });
      setDesigns(d => d.filter(x => x.design_id !== design.design_id));
      toast.success('Diseño eliminado');
    } catch { toast.error('No se pudo eliminar el diseño'); }
  };

  const loadDesign = async (design) => {
    const cfg = design.config;
    setTemplateId(cfg.template_id);
    setConfig(designToState(cfg));
    if (design.include_units) {
      setSelectedUnits(existingIds(cfg.unit_ids));
      setUnitTemplates(Object.fromEntries(Object.entries(cfg.unit_templates).filter(([id]) => existingIds([id]).length)));
    }
    if (customLogo.preview) URL.revokeObjectURL(customLogo.preview);
    setCustomLogo({ id: cfg.custom_logo_id || null, preview: null });
    toast.success(`Diseño “${design.name}” cargado`);
    if (cfg.custom_logo_id) {
      try {
        const res = await axios.get(`${API}/qr-center/poster-logo/${cfg.custom_logo_id}`, { headers, responseType: 'blob' });
        setCustomLogo({ id: cfg.custom_logo_id, preview: URL.createObjectURL(res.data) });
      } catch { /* la vista previa del logo es opcional */ }
    }
  };

  const reprintDesign = async (design) => {
    const ids = design.include_units ? existingIds(design.config.unit_ids) : orderedSelected;
    if (ids.length === 0) {
      toast.error(design.include_units ? 'Las mesas de este diseño ya no existen' : 'Seleccioná las mesas a imprimir');
      return;
    }
    try {
      setReprintingId(design.design_id);
      const body = { ...design.config, unit_ids: ids, unit_templates: design.include_units ? design.config.unit_templates : {} };
      const res = await axios.post(`${API}/qr-center/generate-sheet-with-template`, body, { headers, responseType: 'blob' });
      downloadBlob(res.data, `QRs_${design.name.replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`);
      toast.success(`PDF de “${design.name}” descargado`);
    } catch { toast.error('Error al generar PDF'); }
    finally { setReprintingId(null); }
  };

  const handleUploadLogo = async (file) => {
    if (file.size > 5 * 1024 * 1024) { toast.error('El logo no puede superar 5 MB'); return; }
    try {
      setUploadingLogo(true);
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/qr-center/poster-logo`, form, { headers });
      if (customLogo.preview) URL.revokeObjectURL(customLogo.preview);
      setCustomLogo({ id: res.data.logo_id, preview: URL.createObjectURL(file) });
      toast.success('Logo cargado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDownloadSheet = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/qr-center/generate-sheet-with-template`, payload, { headers, responseType: 'blob' });
      downloadBlob(res.data, `QRs_${(business?.name || 'export').replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`);
      toast.success('PDF generado y descargado');
    } catch {
      toast.error('Error al generar PDF');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadSingleQr = async (unit) => {
    try {
      const color = (HEX.test(config.qrColor) ? config.qrColor : '#111111').replace('#', '');
      const res = await axios.get(`${API}/qr-center/unit/${unit.unit_id}/qr-image?size=400&color=${color}`, { headers, responseType: 'blob' });
      downloadBlob(res.data, `QR_${unit.name}.png`);
      toast.success(`QR de ${unit.name} descargado`);
    } catch {
      toast.error('Error al descargar QR');
    }
  };

  const handleRenew = async () => {
    try {
      setRenewing(true);
      const res = await axios.post(`${API}/qr-center/renew`, { unit_ids: selectedUnits }, { headers });
      toast.success(res.data.message);
      setShowRenewDialog(false);
      await fetchData();
    } catch {
      toast.error('Error al renovar QRs');
    } finally {
      setRenewing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="qr-center">
      <div className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="qr-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center">
              <QrCode className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Centro de QRs</h1>
              <p className="text-sm text-muted-foreground">{business?.name}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" data-testid="qr-selected-count">{selectedUnits.length} seleccionadas</Badge>
            <Button variant="outline" size="sm" onClick={() => setShowRenewDialog(true)} disabled={selectedUnits.length === 0} data-testid="qr-renew-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Renovar QRs
            </Button>
            <Button size="sm" onClick={handleDownloadSheet} disabled={selectedUnits.length === 0 || generating || !templateId} data-testid="qr-generate-pdf-btn">
              {generating
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                : <FileDown className="w-4 h-4 mr-2" />}
              Generar PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs defaultValue="hojas">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="hojas" className="gap-2" data-testid="tab-hojas"><LayoutGrid className="w-4 h-4" />Hojas A4</TabsTrigger>
            <TabsTrigger value="carteles" className="gap-2" data-testid="tab-carteles"><FileImage className="w-4 h-4" />Cartel individual</TabsTrigger>
          </TabsList>

          <TabsContent value="hojas" className="mt-0">
            <SavedDesignsPanel designs={designs} templates={templates} selectedCount={orderedSelected.length}
              saving={savingDesign} reprintingId={reprintingId} onSave={saveDesign} onLoad={loadDesign}
              onReprint={reprintDesign} onUpdate={updateDesign} onDelete={deleteDesign} />
            <div className="grid lg:grid-cols-12 gap-4">
              <div className="lg:col-span-3">
                <TemplateGallery templates={templates} categories={categories} filter={filterCategory}
                  onFilter={setFilterCategory} selectedId={templateId} onSelect={setTemplateId} />
              </div>
              <div className="lg:col-span-3">
                <UnitsPanel units={units} spaces={spaces} filterSpace={filterSpace} onFilterSpace={setFilterSpace}
                  selected={selectedUnits} onToggle={toggleUnit} onAll={selectAll} onNone={() => setSelectedUnits([])}
                  templates={templates} unitTemplates={unitTemplates} onUnitTemplate={setUnitTemplate} onDownload={handleDownloadSingleQr} />
              </div>
              <div className="lg:col-span-3">
                <SheetConfigPanel config={config} setConfig={setConfig} logo={{
                  hasBusinessLogo: !!business?.logo_url, customPreview: customLogo.preview,
                  uploading: uploadingLogo, onUpload: handleUploadLogo
                }} />
              </div>
              <div className="lg:col-span-3">
                <SheetLivePreview preview={preview} loading={previewLoading} page={page} onPage={setPage} selectedCount={selectedUnits.length} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="carteles" className="mt-0">
            <PosterDesigner />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showRenewDialog} onOpenChange={setShowRenewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Renovar acceso de mesas</DialogTitle>
            <DialogDescription>
              Los QR impresos no cambian. Se corta el acceso de los celulares conectados a {selectedUnits.length} mesas abiertas; quien vuelva a escanear entra de nuevo. Los pedidos y la cuenta se mantienen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenewDialog(false)} data-testid="qr-renew-cancel">Cancelar</Button>
            <Button variant="destructive" onClick={handleRenew} disabled={renewing} data-testid="qr-renew-confirm">
              Renovar {selectedUnits.length} QRs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
